import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { BaseDto } from '@models/base/baseDto.model';
import { DetallePeriodoContratoDeuda } from '@models/periodos-contrato/detallePeriodoContratoDeuda.modal';
import { ReporteDeudaPeriodoContratoRequest } from '@models/periodos-contrato/reporteDeudaPeriodoContratoRequest.model';
import { ReporteDeudaPeriodoContratoResponse } from '@models/periodos-contrato/reporteDeudaPeriodoContratoResponse.model';
import { PeriodosContratoService } from '@services/periodos-contrato/periodos-contrato.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { ChartConfiguration, ChartOptions,ChartType } from 'chart.js';
import { NgxSpinnerService } from 'ngx-spinner';
import { isFalsy } from 'utility-types';
import { truncateSync } from 'fs';
import { Color, Label, SingleDataSet, ThemeService } from 'ng2-charts';

@Component({
  selector: 'app-reporte-deuda-periodo-contrato',
  templateUrl: './reporte-deuda-periodo-contrato.component.html',
  styleUrls: ['./reporte-deuda-periodo-contrato.component.css'],
})
export class ReporteDeudaPeriodoContratoComponent {

  form!: FormGroup;
  selectedTab = 0;
  reporteData: ReporteDeudaPeriodoContratoResponse | null = null;
  dataSource = new MatTableDataSource<DetallePeriodoContratoDeuda>();
  displayedColumns: string[] = [
    'numeroContrato',
    // 'nombreInquilino',
    // 'nombrePropietario',
    'propiedad',
    'totalPeriodos',
    'deudaInquilino',
    'porcentajePagadoInquilino',
    'deudaPropietario',
    'porcentajeLiquidadoPropietario'
  ];

  contratoSeleccionado: BaseDto | null = null;

  // Datos para gráficos Chart.js
  // Gráfico de torta - Inquilinos
  pieChartInquilinosData: any = {
    datasets: [{
      data: [],
      backgroundColor: ['#4CAF50', '#f44336'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };
  pieChartInquilinosLabels: string[] = ['Pagado', 'Adeudado'];

  // Gráfico de torta - Propietarios
  pieChartPropietariosData: any = {
    datasets: [{
      data: [],
      backgroundColor: ['#2196F3', '#FF9800'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };
  pieChartPropietariosLabels: string[] = ['Liquidado', 'Por Liquidar'];

  // Gráfico de barras comparativo
  barChartData: any = {
    datasets: [
      {
        label: 'Pagado/Liquidado',
        data: [],
        backgroundColor: '#4CAF50',
        borderColor: '#4CAF50',
        borderWidth: 1
      },
      {
        label: 'Adeudado/Por Liquidar',
        data: [],
        backgroundColor: '#f44336',
        borderColor: '#f44336',
        borderWidth: 1
      }
    ]
  };
  barChartLabels: string[] = ['Inquilinos', 'Propietarios'];

  // Opciones para gráficos
  pieChartType: ChartType = 'pie';
  pieChartLegend = true;
  pieChartPlugins = [];
  pieChartOptions: ChartConfiguration['options'] = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          const label = context.label || '';
          return `${label}: ${context.raw.toFixed(1)}%`;
        }
      }
    }
  }
};

  barChartType: ChartType = 'bar';
  barChartLegend = true;
  barChartPlugins = [];
  barChartOptions: ChartConfiguration['options'] = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top' },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          return `${context.dataset.label}: ${context.raw.toFixed(1)}%`;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: (value: any) => `${value}%`
      }
    }
  } as any
};

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  contratoDetailDto!: BaseDto;

  get f() { return this.form.controls; }

  constructor(
    private fb: FormBuilder,
    private periodosContratoService: PeriodosContratoService,
    private spinner: NgxSpinnerService,
    private snackBar: SnackBarService
  ) {
    this.createForm();
  }

  ngOnInit(): void {

  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private createForm(): void {
    this.form = this.fb.group({
      soloContratosActivos: [true],
      periodoMesDesde: [null],
      periodoAnioDesde: [null],
      periodoMesHasta: [null],
      periodoAnioHasta: [null]
    });
  }

  onContratoSelected(contrato: BaseDto | null): void {
    this.contratoSeleccionado = contrato;
  }

  generarReporte(): void {
    this.spinner.show('spReporteDeuda');

    const request: ReporteDeudaPeriodoContratoRequest = {
      soloContratosActivos: this.f.soloContratosActivos.value,
      idContrato: this.contratoSeleccionado?.id,
      periodoMesDesde: this.f.periodoMesDesde.value,
      periodoAnioDesde: this.f.periodoAnioDesde.value,
      periodoMesHasta: this.f.periodoMesHasta.value,
      periodoAnioHasta: this.f.periodoAnioHasta.value
    };

    this.periodosContratoService.generarReporteDeudaPeriodoContrato(request)
      .subscribe({
        next: (response) => {
          this.reporteData = response;
          this.dataSource.data = response.detalleContratos;
          this.prepararDatosGraficos();
          this.spinner.hide('spReporteDeuda');
        },
        error: (error) => {
          this.spinner.hide('spReporteDeuda');
          this.snackBar.showError(error, 'Error al generar el reporte');
        }
      });
  }

  selectContrato(baseDto: BaseDto | null) {
    if (!isFalsy(baseDto))
      this.contratoDetailDto = baseDto
  }

  private prepararDatosGraficos(): void {
  if (!this.reporteData) return;

  // Totales
  const totalInquilinos = this.reporteData.resumenInquilinos.montoPagado + this.reporteData.resumenInquilinos.montoAdeudado;
  const totalPropietarios = this.reporteData.resumenPropietarios.montoPagado + this.reporteData.resumenPropietarios.montoAdeudado;

  // Datos en % para gráfico de torta - Inquilinos
  this.pieChartInquilinosData = {
    datasets: [{
      data: [
        totalInquilinos ? ((this.reporteData.resumenInquilinos.montoPagado / totalInquilinos) * 100).toFixed(2)  : 0,
        totalInquilinos ? ((this.reporteData.resumenInquilinos.montoAdeudado / totalInquilinos) * 100).toFixed(2)  : 0
      ],
      backgroundColor: ['#4CAF50', '#f44336'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Datos en % para gráfico de torta - Propietarios
  this.pieChartPropietariosData = {
    datasets: [{
      data: [
        totalPropietarios ? ((this.reporteData.resumenPropietarios.montoPagado / totalPropietarios) * 100).toFixed(2)  : 0,
        totalPropietarios ? ((this.reporteData.resumenPropietarios.montoAdeudado / totalPropietarios) * 100).toFixed(2)  : 0
      ],
      backgroundColor: ['#2196F3', '#FF9800'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Gráfico de barras comparativo en %
  this.barChartData = {
    datasets: [
      {
        label: 'Pagado/Liquidado (%)',
        data: [
          totalInquilinos ? ((this.reporteData.resumenInquilinos.montoPagado / totalInquilinos) * 100).toFixed(2)  : 0,
          totalPropietarios ? ((this.reporteData.resumenPropietarios.montoPagado / totalPropietarios) * 100).toFixed(2)  : 0
        ],
        backgroundColor: '#4CAF50',
        borderColor: '#4CAF50',
        borderWidth: 1
      },
      {
        label: 'Adeudado/Por Liquidar (%)',
        data: [
          totalInquilinos ? ((this.reporteData.resumenInquilinos.montoAdeudado / totalInquilinos) * 100).toFixed(2)  : 0,
          totalPropietarios ? ((this.reporteData.resumenPropietarios.montoAdeudado / totalPropietarios) * 100).toFixed(2)  : 0
        ],
        backgroundColor: '#f44336',
        borderColor: '#f44336',
        borderWidth: 1
      }
    ]
  };
}

  limpiarFiltros(): void {
    this.form.reset({
      soloContratosActivos: true,
      periodoMesDesde: null,
      periodoAnioDesde: null,
      periodoMesHasta: null,
      periodoAnioHasta: null
    });
    this.contratoSeleccionado = null;
  }

  exportarExcel(): void {
    // Implementar exportación a Excel
    this.snackBar.showSuccess('Funcionalidad de exportación en desarrollo', 'Info');
  }

  // Helpers para el template
  formatearMoneda(valor: number): string {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(valor);
  }

  formatearPorcentaje(valor: number): string {
    return `${valor.toFixed(2)}%`;
  }

  getEstadoContrato(activo: boolean): string {
    return activo ? 'Activo' : 'Inactivo';
  }

  getClassEstadoContrato(activo: boolean): string {
    return activo ? 'estado-activo' : 'estado-inactivo';
  }

  isFalsy(valor: any): boolean {
    return isFalsy(valor);
  }

  trackByContrato(index: number, item: DetallePeriodoContratoDeuda): number {
    return item.idContrato;
  }

}
